// Typed client for the Playbooks endpoints (PlaybooksController). Thin wrappers
// over apiFetch so the Playbooks pages never repeat path / shape literals —
// mirrors knowledgeApi.ts / agentApi.ts.

import type {
  PlaybookDetail,
  PlaybookRun,
  PlaybookSummary,
  PlaybookWebhookSecret,
  SavePlaybookRequest,
} from '@shared/api-types';
import { apiFetch } from './apiClient';

/** Kanban feed: all playbooks with derived live state. */
export function listPlaybooks(signal?: AbortSignal): Promise<PlaybookSummary[]> {
  return apiFetch<PlaybookSummary[]>('/api/playbooks', { signal });
}

/** Builder detail (definition JSON + missing-reference warnings). */
export function getPlaybook(id: string, signal?: AbortSignal): Promise<PlaybookDetail> {
  return apiFetch<PlaybookDetail>(`/api/playbooks/${id}`, { signal });
}

/** Run history: activations (newest first) with their "Run #N" timelines. */
export function getPlaybookRuns(id: string, signal?: AbortSignal): Promise<PlaybookRun[]> {
  return apiFetch<PlaybookRun[]>(`/api/playbooks/${id}/runs`, { signal });
}

/** Create a playbook (Draft). 400 validation errors surface via ApiError.body. */
export function createPlaybook(body: SavePlaybookRequest): Promise<PlaybookDetail> {
  return apiFetch<PlaybookDetail>('/api/playbooks', { method: 'POST', body });
}

/** Update in place — edits apply from the next run. */
export function updatePlaybook(id: string, body: SavePlaybookRequest): Promise<void> {
  return apiFetch<void>(`/api/playbooks/${id}`, { method: 'PUT', body });
}

/** Delete (409 while a run is in flight). */
export function deletePlaybook(id: string): Promise<void> {
  return apiFetch<void>(`/api/playbooks/${id}`, { method: 'DELETE' });
}

/** Activate a Draft/Done playbook: validates fully and starts/arms the first run. */
export function activatePlaybook(id: string): Promise<void> {
  return apiFetch<void>(`/api/playbooks/${id}/activate`, { method: 'POST' });
}

/** Pause: hold position; in-flight plays finish. */
export function pausePlaybook(id: string): Promise<void> {
  return apiFetch<void>(`/api/playbooks/${id}/pause`, { method: 'POST' });
}

/** Resume a paused playbook from its held position. */
export function resumePlaybook(id: string): Promise<void> {
  return apiFetch<void>(`/api/playbooks/${id}/resume`, { method: 'POST' });
}

/** Stop: finish the activation (reason "stopped manually"). */
export function stopPlaybook(id: string): Promise<void> {
  return apiFetch<void>(`/api/playbooks/${id}/stop`, { method: 'POST' });
}

/** (Re)generate the trigger/signal webhook secret — plaintext shown once. */
export function regenerateWebhookSecret(id: string): Promise<PlaybookWebhookSecret> {
  return apiFetch<PlaybookWebhookSecret>(`/api/playbooks/${id}/webhook-secret`, { method: 'POST' });
}

/** Portable definition for YAML export (references by name/slug, no tenant ids). */
export function exportPlaybook(id: string): Promise<unknown> {
  return apiFetch<unknown>(`/api/playbooks/${id}/export`);
}

/** Import a portable definition (parsed from YAML client-side) as a new Draft. */
export function importPlaybook(portable: unknown): Promise<PlaybookDetail> {
  return apiFetch<PlaybookDetail>('/api/playbooks/import', { method: 'POST', body: portable });
}
